import { useEffect } from "react";

const SITE_NAME = "Mailinflow";
const SITE_URL = "https://mailinflow.com";
const DEFAULT_TITLE =
  "Mailinflow - User-Friendly Email Marketing Tool for Effective Campaigns";
const DEFAULT_DESCRIPTION =
  "Mailinflow offers a powerful and user-friendly email marketing tool for seamless contact management and email template creation. Boost your email campaigns effortlessly with our affordable and easy-to-use platform. Try Mailinflow today!";
const DEFAULT_IMAGE = `${SITE_URL}/favicon/android-chrome-512x512.png`;

function setMetaTag(attr, key, content) {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLinkTag(rel, href) {
  if (!href) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

/**
 * Sets per-page title/meta/OG/Twitter/canonical tags and optional JSON-LD
 * structured data. public/index.html only has one static set of tags for
 * the whole (client-rendered) app, so each public page needs to override
 * them on mount — otherwise every route shares identical SEO metadata.
 */
export default function Seo({ title, description, path, image, jsonLd }) {
  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE;
    const desc = description || DEFAULT_DESCRIPTION;
    const url = path ? `${SITE_URL}${path}` : window.location.href;
    const ogImage = image || DEFAULT_IMAGE;

    document.title = fullTitle;
    setMetaTag("name", "description", desc);
    setMetaTag("property", "og:site_name", SITE_NAME);
    setMetaTag("property", "og:title", fullTitle);
    setMetaTag("property", "og:description", desc);
    setMetaTag("property", "og:url", url);
    setMetaTag("property", "og:type", "website");
    setMetaTag("property", "og:image", ogImage);
    setMetaTag("name", "twitter:card", "summary_large_image");
    setMetaTag("name", "twitter:title", fullTitle);
    setMetaTag("name", "twitter:description", desc);
    setMetaTag("name", "twitter:image", ogImage);
    setLinkTag("canonical", url);
    setJsonLd("seo-jsonld", jsonLd || null);

    return () => {
      // Reset to site defaults on unmount so route changes don't leak
      // stale per-page tags onto the next page.
      document.title = DEFAULT_TITLE;
      setJsonLd("seo-jsonld", null);
    };
  }, [title, description, path, image, jsonLd]);

  return null;
}
